import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BookConsultationButton from "@/components/BookConsultationButton";
import { 
  Phone, PhoneCall, Clock, Users, DollarSign, CheckCircle, ArrowRight,
  Bot, Zap, Calendar, MessageSquare, Shield, Headphones, Star,
  Scale, Briefcase, FileText, UserCheck, AlertCircle, Building2
} from "lucide-react";

const AIReceptionistLawFirmPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = "AI Receptionist for Law Firms Dallas | Legal Virtual Receptionist | Attorney Answering Service DFW";
  }, []);

  const stats = [
    { value: "24/7", label: "Client Intake Available", icon: Clock },
    { value: "After-Hours", label: "Call Coverage", icon: PhoneCall },
    { value: "Every Lead", label: "Captured & Qualified", icon: Users },
    { value: "$0", label: "Missed Opportunities", icon: DollarSign }
  ];

  const practiceAreas = [
    { icon: Scale, name: "Personal Injury", desc: "Accident intake, medical info collection, case evaluation scheduling" },
    { icon: Briefcase, name: "Criminal Defense", desc: "Urgent intake, jail calls, arraignment scheduling, bond info" },
    { icon: FileText, name: "Family Law", desc: "Divorce consultations, custody inquiries, document collection" },
    { icon: Building2, name: "Business Law", desc: "Corporate inquiries, contract questions, consultation booking" },
    { icon: UserCheck, name: "Immigration", desc: "Visa inquiries, case status, document requests, consultations" },
    { icon: AlertCircle, name: "Estate Planning", desc: "Will consultations, trust inquiries, probate questions" }
  ];

  const features = [
    { 
      icon: Phone, 
      title: "24/7 Legal Intake", 
      desc: "Capture potential clients calling after hours, weekends, and holidays. Personal injury calls peak after 6 PM—never miss them." 
    },
    { 
      icon: Shield, 
      title: "Confidential & Compliant", 
      desc: "Attorney-client privilege protocols built-in. HIPAA-compliant for personal injury medical info. Encrypted call handling." 
    },
    { 
      icon: Calendar, 
      title: "Smart Scheduling", 
      desc: "Books consultations directly into attorney calendars. Conflict checking, matter type routing, and priority flagging." 
    },
    { 
      icon: MessageSquare, 
      title: "Intelligent Screening", 
      desc: "Qualifies leads by case type, statute of limitations, jurisdiction, and potential value. Saves attorneys from unqualified consultations." 
    },
    { 
      icon: AlertCircle, 
      title: "Urgent Call Escalation", 
      desc: "Criminal defense calls from jail, emergency restraining orders, and urgent deadlines routed immediately to on-call attorneys." 
    },
    { 
      icon: FileText, 
      title: "Detailed Intake Forms", 
      desc: "Collects accident details, opposing party info, insurance data, and injury descriptions. Sends complete intake to your case management system." 
    }
  ];

  const comparisons = [
    { item: "Full-time Legal Receptionist", traditional: "$45,000-$60,000/year", ai: "$299-$599/month" },
    { item: "After-hours Answering Service", traditional: "$500-$1,500/month", ai: "Included 24/7" },
    { item: "Missed Calls (After Hours)", traditional: "60-80% of leads", ai: "0% - Every call answered" },
    { item: "Intake Quality", traditional: "Varies by staff", ai: "Consistent, thorough" },
    { item: "Spanish-Speaking Intake", traditional: "Extra cost/rare", ai: "Included" },
    { item: "Case Management Integration", traditional: "Manual entry", ai: "Automatic sync" }
  ];

  const faqs = [
    { 
      q: "How does an AI receptionist handle sensitive legal calls?", 
      a: "Our AI legal receptionist is built with attorney-client privilege in mind. All conversations are encrypted, stored securely, and only accessible to authorized firm personnel. The AI is trained to recognize confidential information, never shares case details, and can explain the preliminary nature of the conversation. It's designed to gather intake information while maintaining the confidentiality your clients expect from a law firm."
    },
    { 
      q: "Can AI really qualify personal injury leads effectively?",
      a: "Yes. Our AI is trained specifically for legal intake. It asks about accident date (statute of limitations), fault determination, injury severity, medical treatment, insurance information, and prior representation. It scores leads based on your firm's criteria and flags high-value cases for immediate attorney callback. Firms report 40% improvement in lead quality because every caller gets thorough, consistent screening."
    },
    { 
      q: "What happens when someone calls about a criminal emergency?",
      a: "The AI immediately recognizes urgent criminal matters—calls from jail, pending arrests, or imminent arraignments. It captures essential information (charges, location, bail status) while simultaneously alerting your on-call criminal defense attorney via text, email, or phone. Average escalation time is under 30 seconds. Your attorney has full context before the callback."
    },
    { 
      q: "How does the AI handle non-English speaking potential clients?",
      a: "Our AI receptionist speaks fluent Spanish, which covers the vast majority of non-English legal inquiries in Dallas-Fort Worth. It conducts complete intakes in Spanish, translates notes for attorneys, and can even schedule interpreters for consultations. This dramatically expands your potential client base without hiring bilingual staff."
    },
    { 
      q: "Will potential clients trust speaking to an AI instead of a human?",
      a: "Modern legal AI sounds remarkably natural—most callers don't realize they're speaking to AI. For transparency, we can configure disclosure at any point. More importantly, callers appreciate immediate answers at 2 AM instead of voicemail. Law firms using AI reception report higher client satisfaction because no call goes unanswered and every person feels heard immediately."
    },
    { 
      q: "How does it integrate with our practice management software?",
      a: "We integrate with Clio, MyCase, PracticePanther, Lawmatics, and most major legal CRMs. New leads are automatically created with full intake details, uploaded documents, and call recordings. Appointments sync to attorney calendars. It's not just answering phones—it's automating your entire intake workflow."
    }
  ];

  // Generate schema for the page
  const generateSchemas = () => {
    const schemas = [
      // Service Schema
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "AI Receptionist for Law Firms",
        "description": "24/7 AI-powered virtual receptionist service for law firms in Dallas-Fort Worth. Handles legal intake, appointment scheduling, lead qualification, and emergency escalation for attorneys.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Digital Media Ninja",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "3333 Harry Hines Blvd",
            "addressLocality": "Dallas",
            "addressRegion": "TX",
            "postalCode": "75201"
          }
        },
        "areaServed": [
          { "@type": "City", "name": "Dallas" },
          { "@type": "City", "name": "Fort Worth" },
          { "@type": "City", "name": "Plano" },
          { "@type": "City", "name": "Arlington" },
          { "@type": "City", "name": "Irving" },
          { "@type": "City", "name": "Frisco" },
          { "@type": "City", "name": "McKinney" }
        ],
        "serviceType": "AI Virtual Receptionist",
        "audience": {
          "@type": "Audience",
          "audienceType": "Law Firms, Attorneys, Legal Practices"
        }
      },
      // FAQ Schema
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a
          }
        }))
      },
      // Product Schema with pricing
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "AI Legal Receptionist Service",
        "description": "24/7 AI-powered virtual receptionist for law firms. Handles legal intake, schedules consultations, qualifies leads, and routes urgent matters.",
        "brand": {
          "@type": "Brand",
          "name": "Digital Media Ninja"
        },
        "offers": {
          "@type": "AggregateOffer",
          "lowPrice": "299",
          "highPrice": "599",
          "priceCurrency": "USD",
          "offerCount": "3"
        }
      },
      // WebPage Schema
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "AI Receptionist for Law Firms Dallas",
        "description": "AI-powered virtual receptionist service for Dallas law firms. 24/7 legal intake, appointment scheduling, lead qualification.",
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": ["h1", "h2", ".faq-answer"]
        },
        "mainEntity": {
          "@type": "Service",
          "name": "AI Receptionist for Law Firms"
        }
      },
      // HowTo Schema
      {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to Set Up AI Receptionist for Your Law Firm",
        "description": "Step-by-step guide to implementing AI receptionist for legal practices",
        "step": [
          {
            "@type": "HowToStep",
            "name": "Discovery Call",
            "text": "We learn your practice areas, intake requirements, and call handling preferences"
          },
          {
            "@type": "HowToStep",
            "name": "Legal Configuration",
            "text": "AI is trained on your practice areas, fee structures, and qualification criteria"
          },
          {
            "@type": "HowToStep",
            "name": "Integration Setup",
            "text": "Connect to Clio, MyCase, or your practice management system"
          },
          {
            "@type": "HowToStep",
            "name": "Testing & Launch",
            "text": "Test intake flows, refine responses, go live with confidence"
          }
        ]
      }
    ];
    return schemas;
  };

  const schemas = generateSchemas();

  return (
    <div className="min-h-screen bg-black" data-testid="ai-receptionist-law-firm-page">
      <Helmet>
        <title>AI Receptionist for Law Firms Dallas | Legal Virtual Receptionist | Attorney Answering Service DFW</title>
        <meta name="description" content="AI receptionist for Dallas law firms. 24/7 legal intake, client screening, appointment scheduling & emergency escalation. Personal injury, criminal defense, family law. Save 80% vs hiring. Free demo!" />
        <meta name="keywords" content="AI receptionist law firm Dallas, legal virtual receptionist, attorney answering service DFW, law firm intake automation, AI legal intake, 24/7 legal answering service, personal injury intake AI, criminal defense answering service, family law receptionist Dallas" />
        <link rel="canonical" href="https://digitalmedianinja.com/ai-receptionist-law-firms" />
        <meta property="og:title" content="AI Receptionist for Law Firms Dallas | 24/7 Legal Intake Automation" />
        <meta property="og:description" content="Never miss a potential client. AI receptionist handles legal intake 24/7 for Dallas attorneys. Qualify leads, schedule consultations, route emergencies." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Dallas" />
        {schemas.map((schema, idx) => (
          <script key={idx} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-600/20 via-transparent to-amber-900/10"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full">
                <Scale className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm font-medium">Built for Legal Professionals</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                AI Receptionist for <span className="text-amber-400">Law Firms</span>
              </h1>
              
              <p className="text-xl text-gray-300 max-w-xl">
                Never miss a potential client again. Our AI legal receptionist handles intake 24/7, qualifies leads, schedules consultations, and routes emergencies—even when you're in court.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <BookConsultationButton 
                  meetingType="lawFirm"
                  size="lg"
                  className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-8"
                >
                  Schedule Demo <ArrowRight className="w-5 h-5 ml-2" />
                </BookConsultationButton>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                    <Phone className="w-5 h-5 mr-2" /> Call Us Now
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Card */}
            <div className="relative">
              <Card className="bg-white/5 border-amber-500/20 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center">
                      <Scale className="w-8 h-8 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Legal Intake Automation</h3>
                      <p className="text-amber-400">For Dallas-Fort Worth Law Firms</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {["24/7 client intake for all practice areas", "Lead qualification & case screening", "Emergency escalation protocols", "Clio, MyCase & CRM integration"].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-y border-white/10">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <stat.icon className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Practice Areas */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Practice Areas We Support
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Our AI is trained for specific legal practice areas, understanding the unique intake requirements and urgency of each case type.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {practiceAreas.map((area, i) => (
              <Card key={i} className="bg-white/5 border-white/10 hover:border-amber-500/50 transition-all">
                <CardContent className="p-6">
                  <area.icon className="w-10 h-10 text-amber-400 mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">{area.name}</h3>
                  <p className="text-gray-400">{area.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white/5">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Built for Legal Professionals
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Features designed specifically for law firm intake, compliance, and workflow automation.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="p-6 rounded-xl bg-black/50 border border-white/10">
                <feature.icon className="w-10 h-10 text-amber-400 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Legal Receptionist Cost Comparison
            </h2>
            <p className="text-gray-400">See how AI stacks up against traditional legal reception</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-6 text-gray-400">Expense</th>
                  <th className="text-center py-4 px-6 text-gray-400">Traditional Staff</th>
                  <th className="text-center py-4 px-6 text-amber-400">AI Receptionist</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, i) => (
                  <tr key={i} className="border-b border-white/10">
                    <td className="py-4 px-6 text-white">{row.item}</td>
                    <td className="py-4 px-6 text-center text-gray-400">{row.traditional}</td>
                    <td className="py-4 px-6 text-center text-amber-400 font-semibold">{row.ai}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-white/10 rounded-lg overflow-hidden">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-white/5"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="text-white font-medium">{faq.q}</span>
                  <ArrowRight className={`w-5 h-5 text-amber-400 transition-transform ${openFaq === i ? 'rotate-90' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4 faq-answer">
                    <p className="text-gray-400">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learn More Section */}
      <section className="py-16 border-t border-white/10">
        <div className="container-custom">
          <div className="bg-gradient-to-r from-amber-500/10 to-amber-600/5 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Complete Guide to AI Receptionists for Law Firms
                </h2>
                <p className="text-gray-300 mb-6">
                  Learn everything about implementing AI reception for your legal practice—from ROI calculations 
                  to practice area-specific features, integrations with Clio and MyCase, and real-world success stories.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-amber-400" />
                    Cost comparison: AI vs. traditional receptionist
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-amber-400" />
                    Practice area-specific AI features
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-amber-400" />
                    Legal software integrations guide
                  </li>
                </ul>
                <Link to="/blog/ai-receptionist-for-attorneys-law-firms">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-black font-semibold">
                    Read the Complete Guide <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="hidden md:block">
                <div className="bg-white/5 rounded-xl p-6 border border-amber-500/20">
                  <div className="flex items-center gap-3 mb-4">
                    <FileText className="w-8 h-8 text-amber-400" />
                    <div>
                      <div className="text-white font-semibold">14 Minute Read</div>
                      <div className="text-gray-400 text-sm">Comprehensive Guide</div>
                    </div>
                  </div>
                  <div className="space-y-3 text-sm text-gray-400">
                    <div>✓ ROI Calculator for Law Firms</div>
                    <div>✓ Practice Area Comparison</div>
                    <div>✓ Implementation Checklist</div>
                    <div>✓ FAQ Section with Schema</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-600/20 to-amber-900/20">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Capture Every Legal Lead?
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-8">
            Join Dallas law firms using AI to handle intake 24/7. Never miss a potential client—even when you're in court.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <BookConsultationButton 
              meetingType="aiReceptionist"
              size="lg"
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-8"
            >
              Schedule Your Free Demo
            </BookConsultationButton>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                Contact Our Team
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIReceptionistLawFirmPage;
